import {
  HOUSEHOLD_DOC,
  MEMBER_COLORS,
  isGuestId,
  resolveGamePlayer,
  type HouseholdMember,
} from '@awwyeah/shared';
import { useMemo } from 'react';
import { useYDoc, useYMap } from '../../sync';
import { useCurrentPlayer, useKnownMembers } from './guestPlayer';

/**
 * Own copy of `apps/web/src/modules/apps/useHouseholdRoster.ts` — feature modules may not
 * import each other's internals (docs/ARCHITECTURE.md), only a foundation module's published
 * `index.ts`. Same small-hook-duplication precedent as `lists`/`apps`.
 *
 * WS-23: "me" comes from `useCurrentPlayer()` rather than `useUser()`, so a guest on
 * `/play/:token` sees themselves in every "is it my turn" comparison, and `getMember` takes
 * the game's `guestNames` so the other seat reads as the name that guest chose instead of
 * the generic fallback. Both are additive — a household member's experience is unchanged.
 */

export interface RosterMember {
  id: string;
  name: string;
  color: string;
  avatarEmoji?: string;
}

function fallbackColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return MEMBER_COLORS[hash % MEMBER_COLORS.length] as string;
}

export function useHouseholdRoster(): {
  members: Map<string, RosterMember>;
  meId: string | null;
  getMember: (id: string, guestNames?: Readonly<Record<string, string>>) => RosterMember;
} {
  const me = useCurrentPlayer();
  const knownMembers = useKnownMembers();
  const doc = useYDoc(HOUSEHOLD_DOC);
  const { entries } = useYMap<HouseholdMember>(doc, 'members');

  const members = useMemo(() => {
    const map = new Map<string, RosterMember>();
    for (const [id, member] of entries) {
      map.set(id, {
        id,
        name: member.name,
        color: member.color,
        avatarEmoji: member.avatarEmoji,
      });
    }
    // A guest is never in the household doc, so this is also what puts *them* in their own
    // roster — under their chosen name and the neutral guest colour, not "Member".
    if (me && !map.has(me.id)) {
      map.set(me.id, { id: me.id, name: me.name, color: me.color });
    }
    // …and the member across the table, by the name the server handed the guest page.
    for (const member of knownMembers) {
      if (!map.has(member.id)) map.set(member.id, { ...member });
    }
    return map;
  }, [entries, me, knownMembers]);

  const getMember = (id: string, guestNames?: Readonly<Record<string, string>>): RosterMember => {
    const known = members.get(id);
    if (known) return known;
    // A guest id resolves through the shared resolver (chosen name → "Guest", neutral
    // colour). Any *other* unknown id keeps the pre-WS-23 "Member" + hashed-colour fallback:
    // that one means "a member whose household doc hasn't synced yet", which is a different
    // thing and should not be dressed up as a guest.
    if (isGuestId(id)) {
      const { name, color } = resolveGamePlayer(id, [], guestNames);
      return { id, name, color };
    }
    return { id, name: 'Member', color: fallbackColor(id) };
  };

  return { members, meId: me?.id ?? null, getMember };
}

/**
 * `useHouseholdRoster()` with one game's `guestNames` already bound (WS-23).
 *
 * Every board resolves names for exactly one match, so binding the map once here means the
 * boards keep calling `roster.getMember(id)` with a single argument and stay unchanged — a
 * one-line swap per board instead of threading `meta.guestNames` through thirty call sites.
 * Surfaces that span *several* games (the games index, the archive) can't use this and pass
 * the map explicitly instead.
 */
export function useGameRoster(
  guestNames?: Readonly<Record<string, string>>,
): ReturnType<typeof useHouseholdRoster> {
  const roster = useHouseholdRoster();
  return {
    ...roster,
    getMember: (id: string, override?: Readonly<Record<string, string>>) =>
      roster.getMember(id, override ?? guestNames),
  };
}
